import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { CategoryId } from "@myams/shared";
import { MaIcon, MaTimePill } from "../components/ui";
import { useSignup } from "../lib/queries";

export function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const interests = ((location.state as { interests?: CategoryId[] } | null)?.interests) ?? [];
  const signup = useSignup();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signup.mutateAsync({ email, password, name, handle, interests });
      navigate("/discover");
    } catch { /* shown below */ }
  };

  return (
    <div className="ma-screen" style={{ overflowY: "auto" }}>
      <div style={{
        padding: "calc(env(safe-area-inset-top, 20px) + 34px) 24px 0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <button onClick={() => navigate(-1)} style={{
          background: "transparent", border: "none", padding: 0,
          color: "var(--ma-ink)", fontFamily: "var(--ma-mono)", fontSize: 11,
          letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer",
        }}>
          ← Back
        </button>
        <MaTimePill />
      </div>

      <div style={{ padding: "32px 24px 0" }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--ma-brick)", marginBottom: 10,
        }}>
          step 03 / 03
        </div>
        <div className="ma-display" style={{ fontSize: 44, lineHeight: 0.95, color: "var(--ma-ink)", marginBottom: 12 }}>
          Make it yours
        </div>
        <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink-soft)", marginBottom: 24 }}>
          We'll keep your saves and check-ins synced across devices.
        </div>
      </div>

      <form onSubmit={onSubmit} style={{ padding: "0 24px 24px" }}>
        <label style={fieldWrap}>
          <div className="ma-mono" style={labelStyle}>Name</div>
          <input value={name} onChange={(e) => setName(e.target.value)} required style={inputStyle} />
        </label>

        <label style={fieldWrap}>
          <div className="ma-mono" style={labelStyle}>Handle</div>
          <input
            value={handle} onChange={(e) => setHandle(e.target.value)}
            required pattern="[a-zA-Z0-9_]{2,24}"
            placeholder="lottevdb" style={inputStyle}
          />
        </label>

        <label style={fieldWrap}>
          <div className="ma-mono" style={labelStyle}>Email</div>
          <input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
        </label>

        <label style={fieldWrap}>
          <div className="ma-mono" style={labelStyle}>Password</div>
          <input
            type="password" required minLength={8} autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle}
          />
          <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: "var(--ma-ink-soft)", marginTop: 4 }}>
            min 8 characters
          </div>
        </label>

        {signup.error && (
          <div className="ma-mono" style={{
            fontSize: 11, color: "var(--ma-brick)", marginBottom: 12,
            textTransform: "uppercase", letterSpacing: "0.12em",
          }}>
            {signup.error.message === "email_or_handle_taken"
              ? "That email or handle is taken"
              : signup.error.message === "weak_password"
                ? "Password too short"
                : signup.error.message === "invalid_handle"
                  ? "Handle must be 2-24 letters, digits, or _"
                  : "Could not create account"}
          </div>
        )}

        <button
          type="submit"
          disabled={signup.isPending}
          style={{
            width: "100%", padding: "16px 0",
            background: "var(--ma-brick)", color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)", borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 16,
            letterSpacing: "0.02em", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            opacity: signup.isPending ? 0.6 : 1,
          }}
        >
          {signup.isPending ? "Creating…" : "Create account"}
          <MaIcon name="arrow" size={16} stroke={2} color="var(--ma-paper)" />
        </button>
      </form>

      <div style={{
        padding: "0 24px 36px", textAlign: "center",
        fontFamily: "var(--ma-body)", fontSize: 13, color: "var(--ma-ink-soft)",
      }}>
        Already have an account?{" "}
        <Link to="/login" style={{ color: "var(--ma-brick)", fontWeight: 600 }}>
          Sign in
        </Link>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 10, letterSpacing: "0.16em",
  textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 6,
};
const fieldWrap: React.CSSProperties = { display: "block", marginBottom: 14 };
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "14px",
  background: "var(--ma-cream)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  // 16px is the minimum at which iOS Safari does NOT auto-zoom the
  // viewport on focus. Don't drop below this on any text input.
  fontFamily: "var(--ma-body)", fontSize: 16,
  color: "var(--ma-ink)", boxSizing: "border-box",
};
